import { FC, useCallback, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  ClickAwayListener,
  FormControl,
  InputAdornment,
  Popper,
  Stack,
  Tooltip,
} from '@mui/material';
import { SxProps } from '@mui/system';
import { DateRange, LocalizationProvider, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import dayjs, { Dayjs } from 'dayjs';

import { ReactComponent as CalendarIcon } from '@/assets/icons/calendar-icon.svg';
import { ReactComponent as FormInfo } from '@/assets/icons/form-info-icon.svg';
import { camelToSnakeCase } from '@/shared/lib/strings';
import CalendarDateRangePicker from '@/shared/ui/CalendarDateRangePicker';
import { FormError, FormLabel } from '@/shared/ui/styled';

type Props = {
  id?: string;
  nameFrom: string;
  nameTo: string;
  label?: string;
  disabled?: boolean;
  disablePast?: boolean;
  availability?: string[] | undefined;
  tooltip?: string;
  sx?: SxProps;
  onChange?: (startDate: string, endDate: string) => void;
  'data-testid'?: string;
};

const CLOSE_AFTER_PICK_MS = 200;

const RhfDateRangePicker: FC<Props> = ({
  id,
  nameFrom,
  nameTo,
  label,
  disabled,
  disablePast,
  availability,
  tooltip,
  sx,
  onChange,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    formState: { isSubmitting },
  } = useFormContext();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const from = watch(nameFrom);
  const to = watch(nameTo);

  const availabilitySet = useMemo(() => {
    if (!availability) return new Set<string>();
    return new Set(availability.map((d) => d.split('T')[0]));
  }, [availability]);

  const isDateBlocked = useCallback(
    (date: Dayjs | null): boolean => {
      if (!date || !date.isValid()) return false;
      if (disablePast && date.isBefore(dayjs(), 'day')) return true;
      if (availability !== undefined && !availabilitySet.has(date.format('YYYY-MM-DD')))
        return true;
      return false;
    },
    [disablePast, availability, availabilitySet],
  );

  const dateRangeValue: DateRange<Dayjs> = [from ? dayjs(from) : null, to ? dayjs(to) : null];

  const handleClose = () => setAnchorEl(null);

  const commitRange = (newStart: Dayjs | null, newEnd: Dayjs | null) => {
    const startDate = newStart && !isDateBlocked(newStart) ? newStart.format('YYYY-MM-DD') : '';
    const endDate = newEnd && !isDateBlocked(newEnd) ? newEnd.format('YYYY-MM-DD') : '';

    setValue(nameFrom, startDate, { shouldValidate: true });
    setValue(nameTo, endDate, { shouldValidate: true });

    onChange?.(startDate, endDate);
  };

  return (
    <Controller
      name={nameFrom}
      render={({ fieldState: { error } }) => (
        <FormControl fullWidth error={!!error} sx={{ marginBottom: '1rem' }}>
          {!!label && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <FormLabel
                sx={{
                  fontWeight: 500,
                  lineHeight: '120%',
                  fontSize: '13px',
                  color: '#333239',
                  fontFamily: '"Neue Frutiger World"',
                  letterSpacing: '0.26px',
                }}
              >
                {label}
              </FormLabel>
              {!!tooltip && (
                <Tooltip title={tooltip}>
                  <span>
                    <Button type="button" sx={{ minWidth: 0, padding: '0.25rem' }} disableRipple>
                      <FormInfo />
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              onClick={(event) => {
                if (disabled || isSubmitting) return;
                setAnchorEl(event.currentTarget as HTMLDivElement);
              }}
            >
              <MultiInputDateRangeField
                value={dateRangeValue}
                disabled={isSubmitting || disabled}
                format="DD.MM.YYYY"
                onChange={(value) => commitRange(value?.[0] ?? null, value?.[1] ?? null)}
                slotProps={{
                  textField: {
                    label: undefined,
                    id: id ?? `${camelToSnakeCase(label ?? 'no-label')}-input`,
                    error: !!error,
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon />
                        </InputAdornment>
                      ),
                      endAdornment: null,
                    },
                  },
                }}
                sx={{
                  ...sx,

                  '& .MuiPickersOutlinedInput-root': {
                    fontFamily: 'Neue Frutiger World',
                    padding: '12px 16px !important',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    '&.Mui-error': {
                      border: '1px solid #E9554A !important',
                    },
                  },

                  '& .MuiInputBase-root': {
                    borderRadius: '0.75rem !important',
                  },

                  '& .MuiPickersOutlinedInput-sectionsContainer': {
                    padding: 0,
                    overflow: 'visible',
                    width: 'auto',
                  },

                  '& fieldset': {
                    border: 'none !important',
                    borderRadius: '0.75rem !important',
                  },

                  '& .MuiMultiInputDateRangeField-separator': {
                    display: 'none',
                  },

                  '.MuiFormControl-root:last-of-type': {
                    marginLeft: '16px',
                  },
                }}
                data-testid={
                  dataTestId ?? `${camelToSnakeCase(label ?? 'no-label')}-date-range-picker`
                }
              />
            </Box>
          </LocalizationProvider>

          <ClickAwayListener
            onClickAway={(event) => {
              if (anchorEl && event.target instanceof Node && anchorEl.contains(event.target))
                return;
              handleClose();
            }}
          >
            <Popper
              open={!!anchorEl}
              anchorEl={anchorEl}
              placement="bottom-start"
              modifiers={[
                { name: 'flip', enabled: true },
                { name: 'preventOverflow', enabled: true, options: { padding: 8 } },
              ]}
              sx={{ zIndex: (theme) => theme.zIndex.modal }}
            >
              <CalendarDateRangePicker
                startDate={dateRangeValue[0]}
                endDate={dateRangeValue[1]}
                isDateUnavailable={isDateBlocked}
                disablePast={disablePast}
                minDate={disablePast ? dayjs().startOf('day') : undefined}
                showDateInputs={false}
                onChange={(newStart, newEnd) => {
                  commitRange(newStart, newEnd);
                  if (newStart && newEnd) {
                    // Reflect the pick before closing — committing and closing
                    // in the same tick left the picker looking like the click
                    // did nothing.
                    setTimeout(handleClose, CLOSE_AFTER_PICK_MS);
                  }
                }}
              />
            </Popper>
          </ClickAwayListener>

          {!!error && (
            <FormError>
              {error?.type === 'invalid_type' ? t('errors.required_field') : error.message}
            </FormError>
          )}
        </FormControl>
      )}
    />
  );
};

export default RhfDateRangePicker;
